import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Share2, Download, Link2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { IndexAlertsCard } from "@/components/IndexAlertsCard";
import { CARD } from "@/components/home/shared";
import { LIHero } from "@/components/layerindex/LIHero";
import { DrivingIndex } from "@/components/layerindex/DrivingIndex";
import { HistoryChart } from "@/components/layerindex/HistoryChart";
import { FearGreedCompare } from "@/components/layerindex/FearGreedCompare";
import { ComponentBreakdown } from "@/components/layerindex/ComponentBreakdown";
import { WhyMoved } from "@/components/layerindex/WhyMoved";
import { Watching } from "@/components/layerindex/Watching";
import { ForensicHistory } from "@/components/layerindex/ForensicHistory";
import { MethodologyPanel } from "@/components/layerindex/MethodologyPanel";

const XLogo = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export default function LayerIndexPage() {
  const { data } = useQuery({
    queryKey: ["layer-index-intelligence"],
    queryFn: async () => (await api.get("/layer-index/intelligence")).data,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  const value: number | null = data?.current?.value ?? null;
  const label: string | undefined = data?.current?.label;
  const delta = data?.current?.change_24h;

  const [shareOpen, setShareOpen] = useState(false);
  const shareUrl = `${window.location.origin}/api/v1/s/layer-index`;
  const cardUrl = `${api.defaults.baseURL}/og/layer-index.png`;
  const xText = value != null
    ? `Layer Index today: ${value}/100 — ${label}${delta ? ` (${delta > 0 ? "↑" : "↓"}${Math.abs(delta)} in 24h)` : ""}`
    : "NewsLayer Layer Index";

  const postToX = () => {
    window.open(`https://x.com/intent/post?text=${encodeURIComponent(xText)}&url=${encodeURIComponent(shareUrl)}`,
      "_blank", "noopener");
  };

  const downloadCard = async () => {
    try {
      const r = await fetch(`${cardUrl}?v=${value ?? ""}`);
      if (!r.ok) throw new Error();
      const u = URL.createObjectURL(await r.blob());
      const a = document.createElement("a");
      a.href = u;
      a.download = `newslayer-layer-index-${value ?? "today"}.png`;
      a.click();
      URL.revokeObjectURL(u);
    } catch {
      toast.error("Could not download the card");
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="layer-index-page">
      <Seo title="The Layer Index — Live Crypto Market Mood"
        description="NewsLayer's proprietary 0–100 crypto market mood index — live score, 30-day history, Fear & Greed comparison, component breakdown and full methodology transparency."
        path="/layer-index" />
      <Link to="/" className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 hover:text-white transition-colors mb-6"
        data-testid="index-back-home">
        <ArrowLeft className="h-3 w-3" /> Front Layer
      </Link>

      <LIHero data={data} onShare={() => setShareOpen(true)} />

      {data && (
        <>
          <DrivingIndex components={data.components || []} themes={data.themes || []} />
          <HistoryChart history={data.history || []} events={data.events || []}
            threshold={data.event_threshold ?? 5} />
          <FearGreedCompare fearGreed={data.fear_greed} history={data.history || []}
            divergence={data.divergence} divergenceDays={data.divergence_days || []}
            methodology={data.methodology} />
          <ComponentBreakdown components={data.components || []} methodology={data.methodology}
            value={value} />
          <WhyMoved events={data.events || []} coverage={data.coverage || []}
            threshold={data.event_threshold ?? 5} />
          <Watching watch={data.watch || []} />
          <ForensicHistory history={data.history || []} />
          <MethodologyPanel methodology={data.methodology} dataStatus={data.data_status} />

          <section className="mt-16 sm:mt-24 grid lg:grid-cols-2 gap-6 items-start" data-testid="li-action-band">
            <div className="[&>*]:!mt-0">
              <IndexAlertsCard />
            </div>
            <div className={`${CARD} p-4`} data-testid="li-share-panel">
              <p className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.25em] text-[#A78BFA]">
                <Share2 className="h-3.5 w-3.5 text-[#00E5FF]" /> Share the Layer Index
              </p>
              <p className="text-[13px] text-gray-400 leading-snug mt-2">
                The share card is rendered live from today's score, state and timestamp.
              </p>
              <div className="grid grid-cols-3 gap-2 mt-3">
                <button type="button" onClick={postToX} data-testid="li-share-post-x"
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-white text-black font-semibold text-[12.5px] px-3 py-2 hover:bg-gray-200 transition-colors">
                  <XLogo className="h-3 w-3" /> Post
                </button>
                <button type="button" onClick={downloadCard} data-testid="li-share-download"
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/15 text-gray-200 font-semibold text-[12.5px] px-3 py-2 hover:border-white/40 hover:text-white transition-colors">
                  <Download className="h-3 w-3" /> Save
                </button>
                <button type="button" onClick={copyLink} data-testid="li-share-copy"
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/15 text-gray-200 font-semibold text-[12.5px] px-3 py-2 hover:border-white/40 hover:text-white transition-colors">
                  <Link2 className="h-3 w-3" /> Copy
                </button>
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-1 mt-4 pt-3 border-t border-white/[0.06]">
                <Link to="/" data-testid="li-related-home"
                  className="nl-mono text-[10.5px] font-bold text-[#00E5FF] hover:text-white transition-colors">
                  Layer Index on the Front Layer <ArrowRight className="inline h-3 w-3" />
                </Link>
                <Link to="/markets" data-testid="li-related-markets"
                  className="nl-mono text-[10.5px] font-bold text-[#00E5FF] hover:text-white transition-colors">
                  Live Markets <ArrowRight className="inline h-3 w-3" />
                </Link>
              </div>
            </div>
          </section>
        </>
      )}

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-md bg-[#0b0c10] border-white/10" data-testid="index-share-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Share today's index</DialogTitle>
          </DialogHeader>
          <img src={`${cardUrl}?v=${value ?? ""}`} alt={`Layer Index ${value ?? "—"}/100 — ${label || ""}`}
            data-testid="index-share-preview"
            className="w-full rounded-lg border border-white/10 bg-[#050508]" />
          <div className="grid grid-cols-3 gap-2">
            <button type="button" onClick={postToX} data-testid="index-share-post-x"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-white text-black font-semibold text-[13px] px-3 py-2.5 hover:bg-gray-200 transition-colors">
              <XLogo className="h-3.5 w-3.5" /> Post
            </button>
            <button type="button" onClick={downloadCard} data-testid="index-share-download"
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/15 text-gray-200 font-semibold text-[13px] px-3 py-2.5 hover:border-white/40 hover:text-white transition-colors">
              <Download className="h-3.5 w-3.5" /> Save
            </button>
            <button type="button" onClick={copyLink} data-testid="index-share-copy"
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/15 text-gray-200 font-semibold text-[13px] px-3 py-2.5 hover:border-white/40 hover:text-white transition-colors">
              <Link2 className="h-3.5 w-3.5" /> Copy link
            </button>
          </div>
          <p className="text-[11px] leading-snug text-gray-500">
            Posting on X attaches today's card automatically through the share link preview.
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
